/**
 * ClientTab - Owner, managed-by, contacts, fleet at location, and financial snapshot
 *
 * Blocks: Client Identity (owner), Managed By (conditional),
 * Primary Contact, Additional Contacts, Fleet at Location, Financial Snapshot.
 */

import { toast } from 'sonner'
import { Building2, User, Mail, Phone, UserPlus, Plane, Star, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CLIENT_TYPE_LABELS, CLIENT_STATUS_VARIANTS, type ClientType } from '@/types/clients'
import { formatClientName, formatCurrency } from '@/lib/utils/format-business-data'
import { CompactInfoBlock } from './shared/CompactInfoBlock'
import { ContactCard } from './shared/ContactCard'
import { useClientFleet } from '@/hooks/useClientFleet'
import { useContacts } from '@/hooks/useContacts'
import { useUpdateAircraft } from '@/hooks/useAircraft'
import { useAppContext } from '@/hooks/routing'
import { useQueryClient } from '@tanstack/react-query'
import type { AllocationEnrichedData, AircraftClientInfo, ContactInfo } from '@/types/aircraft-card'

interface ClientTabProps {
  data: AllocationEnrichedData
}

/**
 * Client identity block with relationship badge and "Customer Since"
 */
function ClientIdentityBlock({
  client,
  title,
  formId,
}: {
  client: AircraftClientInfo
  title: string
  formId?: string
}) {
  const clientName = formatClientName(client)
  const typeLabel = CLIENT_TYPE_LABELS[client.clientType as ClientType]
  const isCompany = client.clientType === 'corporate' || client.clientType === 'llc'

  return (
    <CompactInfoBlock title={title} formId={formId}>
      {/* Client name with icon */}
      <div className="flex items-center gap-2">
        {isCompany ? (
          <Building2 className="w-4 h-4 text-muted-foreground" />
        ) : (
          <User className="w-4 h-4 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold">{clientName}</span>
      </div>

      {/* Type badge and status */}
      <div className="flex items-center gap-1.5 mt-1">
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
          {typeLabel}
        </Badge>
        <Badge
          variant={CLIENT_STATUS_VARIANTS[client.status]}
          className="text-[10px] px-1.5 py-0 h-5"
        >
          {client.status}
        </Badge>
      </div>

      {/* Contact info */}
      <div className="flex flex-wrap gap-3 mt-2 text-xs">
        {client.emailPrimary && (
          <a
            href={`mailto:${client.emailPrimary}`}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            title={client.emailPrimary}
          >
            <Mail className="w-3 h-3" />
            <span className="max-w-[100px] truncate">{client.emailPrimary}</span>
          </a>
        )}
        {client.phonePrimary && (
          <a
            href={`tel:${client.phonePrimary}`}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Phone className="w-3 h-3" />
            <span>{client.phonePrimary}</span>
          </a>
        )}
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Empty state when no client is assigned
 */
function NoClientBlock({ title }: { title: string }) {
  return (
    <CompactInfoBlock title={title}>
      <div className="flex items-center gap-2 py-2 text-muted-foreground">
        <UserPlus className="w-4 h-4" />
        <span className="text-sm">No {title.toLowerCase()} assigned</span>
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Primary contact highlighted block
 */
function PrimaryContactBlock({ contact }: { contact: ContactInfo }) {
  const fullName = `${contact.firstName} ${contact.lastName}`.trim()

  return (
    <CompactInfoBlock title="Primary Contact">
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold">{fullName}</span>
          {contact.jobTitle && (
            <span className="text-[10px] text-muted-foreground">
              ({contact.jobTitle})
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-[10px]">
          {contact.emailPrimary && (
            <a
              href={`mailto:${contact.emailPrimary}`}
              className="flex items-center gap-0.5 text-primary hover:underline"
              title={`Email ${fullName}`}
            >
              <Mail className="w-3 h-3" />
              {contact.emailPrimary}
            </a>
          )}
          {contact.phoneMobile && (
            <a
              href={`tel:${contact.phoneMobile}`}
              className="flex items-center gap-0.5 text-primary hover:underline"
              title={`Call ${fullName}`}
            >
              <Phone className="w-3 h-3" />
              {contact.phoneMobile}
            </a>
          )}
        </div>
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Default contact selector for the aircraft
 */
function DefaultContactBlock({
  aircraftId,
  currentContact,
  ownerClientId,
}: {
  aircraftId: string
  currentContact: ContactInfo | null
  ownerClientId: string | undefined
}) {
  const queryClient = useQueryClient()
  const updateAircraft = useUpdateAircraft()
  const { data: contacts } = useContacts(ownerClientId)

  const handleSelect = (contactId: string | null) => {
    updateAircraft.mutate(
      { id: aircraftId, data: { default_contact_id: contactId } },
      {
        onSuccess: () => {
          queryClient.refetchQueries({ queryKey: ['map-allocations'] })
          toast.success(contactId ? 'Default contact updated' : 'Default contact removed')
        },
        onError: (err) => toast.error(`Failed to update: ${err.message}`),
      }
    )
  }

  const availableContacts = contacts ?? []

  return (
    <CompactInfoBlock title="Default Contact">
      {currentContact ? (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <span className="text-xs font-semibold">
              {currentContact.firstName} {currentContact.lastName}
            </span>
            {currentContact.jobTitle && (
              <span className="text-[10px] text-muted-foreground">
                ({currentContact.jobTitle})
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-[10px]">
            {currentContact.emailPrimary && (
              <a
                href={`mailto:${currentContact.emailPrimary}`}
                className="flex items-center gap-0.5 text-primary hover:underline"
              >
                <Mail className="w-3 h-3" />
                {currentContact.emailPrimary}
              </a>
            )}
            {currentContact.phoneMobile && (
              <a
                href={`tel:${currentContact.phoneMobile}`}
                className="flex items-center gap-0.5 text-primary hover:underline"
              >
                <Phone className="w-3 h-3" />
                {currentContact.phoneMobile}
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 py-1 text-muted-foreground">
          <Star className="w-3.5 h-3.5" />
          <span className="text-xs">No default contact</span>
        </div>
      )}

      {/* Dropdown to change default contact */}
      {availableContacts.length > 0 && (
        <div className="mt-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1">
                {currentContact ? 'Change' : 'Set Default'}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {availableContacts.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  onClick={() => handleSelect(c.id)}
                  className="text-xs"
                >
                  <span className="font-medium">
                    {c.first_name} {c.last_name}
                  </span>
                  {c.job_title && (
                    <span className="text-muted-foreground ml-1">({c.job_title})</span>
                  )}
                  {currentContact?.id === c.id && (
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500 ml-auto" />
                  )}
                </DropdownMenuItem>
              ))}
              {currentContact && (
                <DropdownMenuItem
                  onClick={() => handleSelect(null)}
                  className="text-xs text-muted-foreground"
                >
                  Remove default
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </CompactInfoBlock>
  )
}

/**
 * Mini fleet list for client's aircraft at this location
 */
function ClientFleetBlock({ clientId }: { clientId: string }) {
  const { locationId } = useAppContext()
  const { aircraft, isLoading } = useClientFleet(clientId, locationId)

  if (isLoading) {
    return (
      <CompactInfoBlock title="Fleet at Location">
        <p className="text-xs text-muted-foreground/50 text-center py-2">Loading...</p>
      </CompactInfoBlock>
    )
  }

  if (aircraft.length === 0) return null

  const statusColors = {
    lease: 'text-blue-600',
    transient: 'text-amber-600',
    none: 'text-muted-foreground',
  }

  const statusLabels = {
    lease: 'Lease',
    transient: 'Transient',
    none: 'None',
  }

  return (
    <CompactInfoBlock title={`Fleet at Location (${aircraft.length})`}>
      <div className="space-y-1">
        {aircraft.slice(0, 5).map((ac) => (
          <div
            key={ac.id}
            className="flex items-center justify-between text-xs py-0.5 border-b border-border/30 last:border-0"
          >
            <div className="flex items-center gap-1.5">
              <Plane className="w-3 h-3 text-muted-foreground" />
              <span className="font-mono font-medium">{ac.tailNumber}</span>
              {ac.typeName && (
                <span className="text-muted-foreground truncate max-w-[60px]">
                  {ac.typeName}
                </span>
              )}
            </div>
            <span className={`text-[10px] ${statusColors[ac.contractStatus]}`}>
              {statusLabels[ac.contractStatus]}
            </span>
          </div>
        ))}
        {aircraft.length > 5 && (
          <p className="text-[10px] text-muted-foreground/60 text-center">
            +{aircraft.length - 5} more
          </p>
        )}
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Financial snapshot from lease + fuel totals
 */
function FinancialSnapshotBlock({ data }: { data: AllocationEnrichedData }) {
  const monthlyRevenue = data.leaseContract?.monthlyRate ?? 0

  if (!monthlyRevenue && !data.transientStay?.totalAmount) return null

  return (
    <CompactInfoBlock title="Revenue Snapshot">
      <div className="space-y-1">
        {data.leaseContract && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Monthly Lease</span>
            <span className="font-medium">{formatCurrency(monthlyRevenue)}</span>
          </div>
        )}
        {data.transientStay?.totalAmount && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Booking Total</span>
            <span className="font-medium">{formatCurrency(data.transientStay.totalAmount)}</span>
          </div>
        )}
        {data.leaseContract && monthlyRevenue > 0 && (
          <div className="flex items-center justify-between text-xs pt-1 border-t border-border/30">
            <span className="text-muted-foreground">Est. Annual</span>
            <span className="font-semibold">{formatCurrency(monthlyRevenue * 12)}</span>
          </div>
        )}
      </div>
    </CompactInfoBlock>
  )
}

export function ClientTab({ data }: ClientTabProps) {
  const { aircraft, ownerContacts, operatorContacts } = data

  // Check if operator is different from owner
  const hasOperator = aircraft.operator && aircraft.operator.id !== aircraft.owner?.id

  // Split contacts into primary and additional
  const allContacts = ownerContacts ?? operatorContacts ?? []
  const primaryContact = allContacts.find((c) => c.isPrimaryContact) || null
  const additionalContacts = allContacts.filter((c) => !c.isPrimaryContact)

  return (
    <div className="space-y-0">
      {/* Block 1: Owner Identity */}
      {aircraft.owner ? (
        <ClientIdentityBlock client={aircraft.owner} title="Owner" formId="client-editor" />
      ) : (
        <NoClientBlock title="Owner" />
      )}

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 2: Managed By (only if different from owner) */}
      {hasOperator && aircraft.operator && (
        <>
          <ClientIdentityBlock client={aircraft.operator} title="Managed By" formId="client-editor" />
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Block 2.5: Default Contact for aircraft */}
      <DefaultContactBlock
        aircraftId={aircraft.id}
        currentContact={aircraft.defaultContact}
        ownerClientId={aircraft.owner?.id}
      />
      <div className="border-t border-border/50" />

      {/* Block 3: Primary Contact (highlighted) */}
      {primaryContact && (
        <>
          <PrimaryContactBlock contact={primaryContact} />
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Block 4: Additional Contacts */}
      {additionalContacts.length > 0 && (
        <>
          <CompactInfoBlock title={`Contacts (${additionalContacts.length})`}>
            <div className="space-y-0.5">
              {additionalContacts.map((contact) => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          </CompactInfoBlock>
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Show "no contacts" only when there are none at all */}
      {allContacts.length === 0 && (
        <>
          <CompactInfoBlock title="Contacts">
            <div className="flex items-center gap-2 py-2 text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="text-sm">No contacts assigned</span>
            </div>
          </CompactInfoBlock>
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Block 5: Fleet at Location */}
      {aircraft.owner && (
        <>
          <ClientFleetBlock clientId={aircraft.owner.id} />
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Block 6: Financial Snapshot */}
      <FinancialSnapshotBlock data={data} />
    </div>
  )
}
